import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/**
 * "Minimalista" template — no colored header, thin separators, black/gray only.
 * Cor da marca aparece só em uma linha accent fina.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const { primaryColor, grayLight, grayText, darkText } = buildPalette(
    data.brandColorRGB,
  );
  const contentWidth = pageWidth - margin * 2;

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === HEADER (simple, top-left company data, top-right logo) ===
  let yPos = margin + 4;

  doc.setTextColor(...darkText);
  doc.setFont('helvetica', 'bold');
  // A logo (20mm) fica no canto direito da MESMA linha, quando existe.
  const tituloCabecalho = fitHeaderTitle(
    doc,
    resolveDocumentTitle(data),
    contentWidth - (logoBase64 ? 24 : 0),
    20,
  );
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, margin, yPos);
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: margin, y: yPos, maxWidth: contentWidth - (logoBase64 ? 24 : 0), color: grayText,
  });

  if (logoBase64) {
    try {
      const logoSize = 20;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, pageWidth - margin - logoSize, margin, logoSize, logoSize);
    } catch {
      /* ignore */
    }
  }

  yPos += 8;
  if (data.company?.name) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text(data.company.name, margin, yPos);
    yPos += 5;
  }

  const companyLines: string[] = [];
  if (data.company?.cnpj) companyLines.push(`CNPJ ${data.company.cnpj}`);
  if (data.company?.address) companyLines.push(data.company.address);
  const contact: string[] = [];
  if (data.company?.phone) contact.push(data.company.phone);
  if (data.company?.email) contact.push(data.company.email);
  if (contact.length) companyLines.push(contact.join(' · '));

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  companyLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, contentWidth - 30)[0];
    doc.text(wrapped, margin, yPos);
    yPos += 4;
  });

  // Thin accent separator
  yPos += 4;
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.6);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // === CLIENT INFO (plain rows) ===
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkText);
  doc.text('PARA', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(data.client.name || 'Não informado', margin + 16, yPos);

  doc.setFont('helvetica', 'bold');
  doc.text('DATA', pageWidth - margin - 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatQuoteDate(data.quoteDate), pageWidth - margin - 22, yPos);
  yPos += 5;

  if (data.client.phone) {
    doc.setFont('helvetica', 'bold');
    doc.text('TEL', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.client.phone, margin + 16, yPos);
    yPos += 5;
  }
  if (data.client.document) {
    doc.setFont('helvetica', 'bold');
    doc.text(detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ' : 'CPF', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDocument(data.client.document), margin + 16, yPos);
    yPos += 5;
  }
  if (data.client.address) {
    doc.setFont('helvetica', 'bold');
    doc.text('END', margin, yPos);
    doc.setFont('helvetica', 'normal');
    const addr = doc.splitTextToSize(data.client.address, contentWidth - 20)[0];
    doc.text(addr, margin + 16, yPos);
    yPos += 5;
  }
  if (data.validityDays && data.quoteDate) {
    doc.setFont('helvetica', 'bold');
    doc.text('VÁLIDO', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(`até ${calcValidityDate(data.quoteDate, data.validityDays)}`, margin + 16, yPos);
    yPos += 5;
  }
  if (data.reference) {
    doc.setFont('helvetica', 'bold');
    doc.text('REF', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(doc.splitTextToSize(data.reference, contentWidth - 20)[0], margin + 16, yPos);
    yPos += 5;
  }

  yPos += 6;

  // === TABLE (borderless, only horizontal lines) ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 14, halign: 'center' as const },
        2: { cellWidth: 12, halign: 'center' as const },
        3: { cellWidth: 26, halign: 'right' as const },
        4: { cellWidth: 20, halign: 'center' as const },
        5: { cellWidth: 28, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 16, halign: 'center' as const },
        2: { cellWidth: 12, halign: 'center' as const },
        3: { cellWidth: 30, halign: 'right' as const },
        4: { cellWidth: 30, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'plain',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: darkText,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
      lineColor: darkText,
      lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 },
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: grayLight,
      lineWidth: { bottom: 0.2, top: 0, left: 0, right: 0 },
    },
    columnStyles,
    margin: { left: margin, right: margin },
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 4;

  // === TOTAL (discreto, alinhado à direita, sem fundo) ===
  const totals = pdfQuoteTotals(data);
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: 70,
  });

  doc.setDrawColor(...darkText);
  doc.setLineWidth(0.5);
  doc.line(pageWidth - margin - 70, yPos, pageWidth - margin, yPos);

  yPos += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  doc.text('TOTAL', pageWidth - margin - 70, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...darkText);
  doc.text(formatCurrency(totals.total), pageWidth - margin, yPos, { align: 'right' });

  yPos += 10;

  // === OBSERVATIONS / WARRANTY / NOTES ===
  if (data.warranty || data.notes) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('Observações', margin, yPos);
    yPos += 5;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);

    if (data.warranty) {
      const lines = doc.splitTextToSize(`Garantia: ${data.warranty}`, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 2;
    }
    if (data.notes) {
      const lines = doc.splitTextToSize(data.notes, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 2;
    }
    yPos += 4;
  }

  // === PIX / PHOTOS / SIGNATURE / FOOTER ===
  // Formas aceitas + condições de pagamento. O retorno avança o `yPos`:
  // tudo abaixo (PIX, fotos, assinatura) é posicionado por coordenada.
  // === DIAGNÓSTICO TÉCNICO (Ordem de Serviço) ===
  // Só desenha quando a OS tem os campos preenchidos; devolve o yPos avançado
  // (jsPDF não tem layout — armadilha #13).
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  if (data.termsConditions) {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = margin;
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('Termos e condições', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth);
    doc.text(lines, margin, yPos);
    yPos += 3.5 * lines.length + 4;
  }

  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin,
    pageWidth,
    pageHeight,
  });

  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}
